import { PractitionerSearchResultApi } from './../../utils/interfaces/new/PractitionerSearchResultApi';
import { PractitionerSearchParamsAPICRYPT } from './../mss/mss-service.abstract';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { InfoAnnuaire } from 'src/app/utils/interfaces/new/info-annuaire.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { PractitionerSearchResult } from 'src/app/utils/interfaces/new/PractitionerSearchResult';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../filtres/filtres.service';
import { PractitionerSearchParams } from '../mss/mss-service.abstract';
import { SnackbarService } from '../snackbar/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class AnnuaireProfessionnelsSanteService {

  private _searchPractitionerResult: PractitionerSearchResult;
  private _searchPractitionerResultApi: PractitionerSearchResultApi;

  private isMock = environment.mock;

  private uriAnn = `${environment.basePath}/hypermed/ann/`;

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };

  constructor(
    private _http: HttpClient,
    private _snack: SnackbarService,
    private _filtres: FiltresService
  ) { }

  /**
   * Encode les caractères spéciaux LDAP pour éviter les injections.
   * Ex: `Dupont)(uid=*)` => `Dupont%29%28uid%3D%2A%29`
   */
  private sanitizeLdapValue(value: string): string {
    if (!value) { return value; }
    return value
      .replace(/%/g, '%25')
      .replace(/\\/g, '%5C')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
      .replace(/\*/g, '%2A')
      .replace(/=/g, '%3D')
      .replace(/,/g, '%2C')
      .replace(/;/g, '%3B')
      .replace(/\+/g, '%2B')
      .replace(/#/g, '%23')
      .replace(/&/g, '%26')
      .replace(/\?/g, '%3F')
      .replace(/\x00/g, '%00');
  }

  public async findInfoByRpps(rpps: string): Promise<InfoAnnuaire | undefined> {
    try {
      const res = await this._http.get(this.uriAnn + rpps, this.httpOptions).toPromise();
      const info = res.body as InfoAnnuaire;
      return info;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      return undefined;
    }
  }

  public async searchAnnuaireSante(filtre: InfoAnnuaire, page: Filtres = {
    currentPage: 1,
    perpage: -1,
  }): Promise<Paginated<InfoAnnuaire>> {
    try {
      this.httpOptions.headers = this._filtres.setHeaders(page);
      const res = await this._http.post(this.uriAnn, filtre, this.httpOptions).toPromise();
      const info = res.body as InfoAnnuaire[];
      const paginated: Paginated<InfoAnnuaire> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: info
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      return {
        page: 0,
        perpage: 0,
        total: 0,
        objects: [],
      } as Paginated<InfoAnnuaire>;
    }
  }

  /**
   *
   * @param params Paramètres de recherche
   * @param limit Nombre de résultats par page
   * @param offset Page à laquelle accéder
   * @param doForceSearch Pour effectuer une nouvelle recherche et ne pas utiliser le résultat présent en cache, utiliser la valeur true
   * @returns Objet contenant la liste de résultat, la pagination ainsi que l'information déterminant si le résultat est tronqué
   */
  public async searchProfessionals(params: PractitionerSearchParams, limit = 10, offset = 0,
                                   doForceSearch = false): Promise<PractitionerSearchResult>{
    const paginateResult = (_result: PractitionerSearchResult, _limit: number, _offset: number): PractitionerSearchResult => {
      // console.log("SLICE : ", _result.result);
      // console.log("LIMIT: ", _limit);
      // console.log("OFFSET: ", _offset);
      let result;
      if (_limit === -1) {
        result = _result.result;
      } else {
        result = _result.result.slice(_offset * _limit, (_offset * _limit) + _limit);
      }
      return {
        result,
        troncated: _result.troncated,
        page: _offset,
        perpage: _limit,
        total: _result.result.length
      };
    };

    return new Promise<PractitionerSearchResult>(async (resolve, reject) => {
      try {

        if ((this._searchPractitionerResult && offset > 0) || !doForceSearch) {
          resolve(paginateResult({...this._searchPractitionerResult}, limit, offset));
          return;
        } else {
          this._searchPractitionerResult = undefined;
          offset = 0;
        }

        const queryParams: Record<string, string> = {};

        if (params.city && params.city !== '') {
          queryParams['city'] = this.sanitizeLdapValue(params.city);
        }
        if (params.firstName && params.firstName !== '') {
          queryParams['firstName'] = this.sanitizeLdapValue(params.firstName);
        }
        if (params.practitionerName && params.practitionerName !== '') {
          queryParams['practitionerName'] = this.sanitizeLdapValue(params.practitionerName);
        }
        if (params.profession && params.profession !== '') {
          queryParams['profession'] = this.sanitizeLdapValue(params.profession);
        }
        if (params.zip && params.zip !== '') {
          queryParams['zip'] = this.sanitizeLdapValue(params.zip);
        }
        if (params.rpps && params.rpps !== '') {
          queryParams['rpps'] = this.sanitizeLdapValue(params.rpps);
        }
        if (params.speciality && params.speciality !== '') {
          queryParams['speciality'] = this.sanitizeLdapValue(params.speciality);
        }
        if (params.structureOrAddress && params.structureOrAddress !== '') {
          queryParams['structureOrAddress'] = this.sanitizeLdapValue(params.structureOrAddress);
        }
        if (params.emailMSS && params.emailMSS !== '') {
          queryParams['emailMSS'] = encodeURIComponent(this.sanitizeLdapValue(params.emailMSS));
        }

        const httpOptions = {
          withCredentials: true,
          observe: 'response' as 'response',
          headers: new HttpHeaders(),
          params: queryParams
        };

        const result = await this._http.get<PractitionerSearchResult>(
          `${environment.basePath}/messagerie/annuaire/search`,
          httpOptions
        ).toPromise();

        // console.log("BODY RESULT : ", result.body);

        this._searchPractitionerResult = result.body;

        resolve(paginateResult({...this._searchPractitionerResult}, limit, offset));
      } catch (e) {
        reject(e);
      }
    });
  }

  public async searchProfessionalsApi(params: PractitionerSearchParamsAPICRYPT, limit = 10, offset = 0,
                                   doForceSearch = false): Promise<PractitionerSearchResultApi>{
    const paginateResult = (_result: PractitionerSearchResultApi, _limit: number, _offset: number): PractitionerSearchResultApi => {
      // console.log("SLICE : ", _result.result);
      // console.log("LIMIT: ", _limit);
      // console.log("OFFSET: ", _offset);
      let result;
      if (_limit === -1) {
        result = _result.result;
      } else {
        result = _result.result.slice(_offset * _limit, (_offset * _limit) + _limit);
      }
      return {
        result,
        troncated: _result.troncated,
        page: _offset,
        perpage: _limit,
        total: _result.result.length
      };
    };

    return new Promise<PractitionerSearchResultApi>(async (resolve, reject) => {
      try {

        if ((this._searchPractitionerResult && offset > 0) || !doForceSearch) {
          resolve(paginateResult({...this._searchPractitionerResult}, limit, offset));
          return;
        } else {
          this._searchPractitionerResult = undefined;
          offset = 0;
        }

        const queryParams: Record<string, string> = {};

        if (params.ville && params.ville !== '') {
          queryParams['ville'] = this.sanitizeLdapValue(params.ville);
        }
        if (params.firstName && params.firstName !== '') {
          queryParams['firstName'] = this.sanitizeLdapValue(params.firstName);
        }
        if (params.practitionerName && params.practitionerName !== '') {
          queryParams['practitionerName'] = this.sanitizeLdapValue(params.practitionerName);
        }
        if (params.specialite && params.specialite !== '') {
          queryParams['specialite'] = this.sanitizeLdapValue(params.specialite);
        }
        if (params.postalCode && params.postalCode !== '') {
          queryParams['postalCode'] = this.sanitizeLdapValue(params.postalCode);
        }
        if (params.rpps && params.rpps !== '') {
          queryParams['rpps'] = this.sanitizeLdapValue(params.rpps);
        }

        const httpOptions = {
          withCredentials: true,
          observe: 'response' as 'response',
          headers: new HttpHeaders(),
          params: queryParams
        };

        const result = await this._http.get<PractitionerSearchResultApi>(
          `${environment.basePath}/messagerie/annuaire/searchApi`,
          httpOptions
        ).toPromise();

        // console.log("BODY RESULT : ", result.body);

        this._searchPractitionerResultApi = result.body;

        resolve(paginateResult({...this._searchPractitionerResultApi}, limit, offset));
      } catch (e) {
        reject(e);
      }
    });
  }
}
